import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const DATA_NODES = [
  {
    id: 'aurora',
    label: 'Aurora',
    description: 'Sistema de Governança',
    detail: 'Onde as 3 Leis da Vérixa são definidas. Estrutura de avaliação de respostas, ética e coerência. Em desenvolvimento — conceito definido, integração em progresso.',
    color: '#7a00ff',
    status: 'Em desenvolvimento',
  },
  {
    id: 'kaira',
    label: 'Kaira',
    description: 'Núcleo Criativo',
    detail: 'Módulo de criação multimodal planejado. Texto já disponível. Imagem, áudio e vídeo sendo integrados progressivamente ao pipeline.',
    color: '#00f0ff',
    status: 'Parcial',
  },
  {
    id: 'averexa',
    label: 'Averexa',
    description: 'Terminal de Análise',
    detail: 'Camada de análise estratégica e tomada de decisão contextual. Conceito arquitetado — implementação iniciada.',
    color: '#ff00c8',
    status: 'Planejado',
  },
];

type WorldState = 'idle' | 'entering' | 'active';

export default function VerixaWorld() {
  const sectionRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [activeNode, setActiveNode] = useState<string | null>(null);
  const [mouseX, setMouseX] = useState(0);
  const [worldState, setWorldState] = useState<WorldState>('idle');
  const gridRef = useRef<HTMLDivElement>(null);
  const animFrameRef = useRef<number>(0);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    gsap.fromTo('.world-title', { opacity: 0, y: 30 }, {
      opacity: 1, y: 0, duration: 1, ease: 'power3.out',
      scrollTrigger: { trigger: section, start: 'top 70%', toggleActions: 'play none none none' },
    });

    gsap.fromTo('.data-node', { opacity: 0, y: 40, scale: 0.95 }, {
      opacity: 1, y: 0, scale: 1, duration: 0.8, stagger: 0.2, ease: 'power3.out',
      scrollTrigger: { trigger: section, start: 'top 55%', toggleActions: 'play none none none' },
    });
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const section = sectionRef.current;
      if (!section) return;
      const rect = section.getBoundingClientRect();
      if (e.clientY < rect.top || e.clientY > rect.bottom) return;
      setMouseX(((e.clientX - rect.left) / rect.width - 0.5) * 40);
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Canvas particle effect when world is "active"
  useEffect(() => {
    if (worldState !== 'active') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const particles: Array<{ x: number; y: number; vx: number; vy: number; r: number; color: string; alpha: number }> = [];
    const colors = ['#7a00ff', '#00f0ff', '#ff00c8', '#00ff9c'];

    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.6,
        vy: (Math.random() - 0.5) * 0.6,
        r: Math.random() * 2 + 0.5,
        color: colors[Math.floor(Math.random() * colors.length)],
        alpha: Math.random() * 0.6 + 0.2,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color + Math.floor(p.alpha * 255).toString(16).padStart(2, '0');
        ctx.fill();
      });
      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 80) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(0,240,255,${(1 - dist / 80) * 0.12})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
      animFrameRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [worldState]);

  const handleEnterWorld = () => {
    setWorldState('entering');
    setTimeout(() => setWorldState('active'), 1800);
  };

  return (
    <section
      id="world"
      ref={sectionRef}
      className="relative w-full min-h-screen py-32 px-6 overflow-hidden"
      style={{ background: '#050505', zIndex: 5 }}
    >
      {/* Holographic Grid */}
      <div ref={gridRef} className="absolute inset-0" style={{ perspective: '1200px', zIndex: 0 }}>
        <div
          className="absolute w-[200%] h-[50%] left-[-50%] bottom-0 animate-pulse-grid"
          style={{
            backgroundImage: 'linear-gradient(rgba(0,240,255,0.25) 1px, transparent 1px), linear-gradient(90deg, rgba(0,240,255,0.25) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
            transformOrigin: '50% 100%',
            transform: `rotateX(60deg) translateX(${mouseX}px)`,
            maskImage: 'radial-gradient(ellipse 80% 50% at 50% 100%, black 30%, transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse 80% 50% at 50% 100%, black 30%, transparent 70%)',
          }}
        />
        <div
          className="absolute w-[200%] h-[50%] left-[-50%] top-0 animate-pulse-grid"
          style={{
            backgroundImage: 'linear-gradient(rgba(0,240,255,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(0,240,255,0.15) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
            transformOrigin: '50% 0%',
            transform: `rotateX(-70deg) translateX(${mouseX}px)`,
            maskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 30%, transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 30%, transparent 70%)',
          }}
        />
      </div>

      {/* Content */}
      <div className="relative max-w-6xl mx-auto" style={{ zIndex: 2 }}>
        {/* Header */}
        <div className="world-title text-center mb-12">
          <span className="text-[11px] uppercase tracking-[0.25em] font-semibold mb-4 block" style={{ color: '#00f0ff' }}>
            Explore
          </span>
          <h2 className="font-syncopate text-white uppercase" style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)', textShadow: '0 0 60px rgba(0,240,255,0.2)' }}>
            Vérixa World
          </h2>
          <p className="mt-4 text-slate-400 max-w-lg mx-auto">
            Ambiente imersivo em desenvolvimento. Explore a arquitetura modular da Vérixa.
          </p>
          <div className="flex justify-center mt-5 gap-3">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-pill text-[10px] uppercase tracking-[0.15em] font-semibold border" style={{ background: 'rgba(255,165,0,0.06)', borderColor: 'rgba(255,165,0,0.3)', color: '#ffaa00' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#ffaa00]" />
              Preview
            </span>
          </div>
        </div>

        {/* World Entry Experience */}
        {worldState === 'idle' && (
          <div className="flex justify-center mb-16">
            <button
              onClick={handleEnterWorld}
              className="group relative px-10 py-5 rounded-pill font-syncopate text-sm uppercase tracking-[0.15em] text-white transition-all duration-500 hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, rgba(122,0,255,0.2), rgba(0,240,255,0.1))',
                border: '1px solid rgba(0,240,255,0.3)',
                boxShadow: '0 0 40px rgba(0,240,255,0.1)',
              }}
            >
              <span className="relative z-10">Entrar no Mundo</span>
              <div
                className="absolute inset-0 rounded-pill opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: 'linear-gradient(135deg, rgba(122,0,255,0.3), rgba(0,240,255,0.15))' }}
              />
            </button>
          </div>
        )}

        {worldState === 'entering' && (
          <div className="flex flex-col items-center mb-16 gap-6">
            <div className="relative w-20 h-20">
              <div className="absolute inset-0 rounded-full border-2 border-[#00f0ff]/30 animate-ping" />
              <div className="absolute inset-2 rounded-full border border-[#7a00ff]/50 animate-spin" style={{ animationDuration: '3s' }} />
              <div className="absolute inset-4 rounded-full bg-[#00f0ff]/10 animate-pulse" />
            </div>
            <p className="text-[#00f0ff] text-xs uppercase tracking-[0.2em] animate-pulse">Inicializando ambiente...</p>
          </div>
        )}

        {worldState === 'active' && (
          <div className="mb-12">
            <div
              className="relative w-full rounded-panel overflow-hidden mb-6"
              style={{ height: '200px', border: '1px solid rgba(0,240,255,0.2)', background: 'rgba(0,0,0,0.6)' }}
            >
              <canvas ref={canvasRef} className="w-full h-full" style={{ display: 'block' }} />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#00f0ff]/40">Ambiente ativo — nós interativos abaixo</p>
              </div>
              <div className="absolute top-3 right-3 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00ff9c] animate-pulse" />
                <span className="text-[9px] text-[#00ff9c]/60 uppercase tracking-[0.15em]">Online</span>
              </div>
            </div>
          </div>
        )}

        {/* Data Nodes */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {DATA_NODES.map((node) => (
            <div
              key={node.id}
              className="data-node group relative p-8 rounded-panel cursor-pointer transition-all duration-500"
              style={{
                background: activeNode === node.id
                  ? `linear-gradient(135deg, ${node.color}15, rgba(255,255,255,0.03))`
                  : 'rgba(255,255,255,0.03)',
                backdropFilter: 'blur(40px)',
                WebkitBackdropFilter: 'blur(40px)',
                border: `1px solid ${activeNode === node.id ? node.color + '50' : 'rgba(255,255,255,0.08)'}`,
                boxShadow: activeNode === node.id ? `0 0 40px ${node.color}20` : 'none',
              }}
              onClick={() => setActiveNode(activeNode === node.id ? null : node.id)}
            >
              <div className="absolute inset-0 rounded-panel opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" style={{ boxShadow: `inset 0 0 60px ${node.color}10` }} />

              <div className="flex items-start justify-between mb-6">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${node.color}15`, border: `1px solid ${node.color}40`, boxShadow: `0 0 20px ${node.color}20` }}>
                  <span className="text-sm font-bold" style={{ color: node.color }}>
                    {node.id === 'aurora' ? '01' : node.id === 'kaira' ? '02' : '03'}
                  </span>
                </div>
                <span className="text-[9px] uppercase tracking-[0.1em] px-2 py-1 rounded-pill" style={{ background: `${node.color}15`, color: node.color, border: `1px solid ${node.color}30` }}>
                  {node.status}
                </span>
              </div>

              <h3 className="font-syncopate text-lg uppercase tracking-[0.05em] mb-2" style={{ color: node.color }}>
                {node.label}
              </h3>
              <p className="text-white/80 text-sm font-medium mb-3">{node.description}</p>

              <div className="overflow-hidden transition-all duration-500" style={{ maxHeight: activeNode === node.id ? '200px' : '0px', opacity: activeNode === node.id ? 1 : 0 }}>
                <p className="text-slate-400 text-sm leading-relaxed pt-3 border-t border-white/10">{node.detail}</p>
              </div>

              <div className="mt-4 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: node.color }} />
                <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
                  {activeNode === node.id ? 'Fechar' : 'Explorar'}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Connection Lines */}
        <div className="hidden md:flex justify-center mt-8">
          <svg width="600" height="40" viewBox="0 0 600 40" className="opacity-30">
            <line x1="100" y1="20" x2="300" y2="20" stroke="#00f0ff" strokeWidth="1" strokeDasharray="4 4">
              <animate attributeName="stroke-dashoffset" from="0" to="-16" dur="2s" repeatCount="indefinite" />
            </line>
            <line x1="300" y1="20" x2="500" y2="20" stroke="#00f0ff" strokeWidth="1" strokeDasharray="4 4">
              <animate attributeName="stroke-dashoffset" from="0" to="-16" dur="2s" repeatCount="indefinite" />
            </line>
            <circle cx="100" cy="20" r="4" fill="#7a00ff"><animate attributeName="opacity" values="0.5;1;0.5" dur="3s" repeatCount="indefinite" /></circle>
            <circle cx="300" cy="20" r="4" fill="#00f0ff"><animate attributeName="opacity" values="0.5;1;0.5" dur="3s" repeatCount="indefinite" /></circle>
            <circle cx="500" cy="20" r="4" fill="#ff00c8"><animate attributeName="opacity" values="0.5;1;0.5" dur="3s" repeatCount="indefinite" /></circle>
          </svg>
        </div>
      </div>
    </section>
  );
}

